/**
 * OneTimeGrantDialog — rental-wizard scoped one-time grant capture dialog.
 * STORY-1305 Task 4 (Sprint 17 W1) + D1=C review 2026-05-13.
 *
 * Lifecycle (D1=C): the dialog does NOT call the backend. It captures
 * granteeData into `wizardStore.pendingOneTimeGrant` and flips `grantId` to
 * the `PENDING_ONE_TIME_SENTINEL` marker. RentalWizardPage.handleComplete
 * materializes the grant AFTER POST /rentals returns the new rental id
 * (single rental+grant flow, no orphan grants from cancelled wizards).
 *
 * MVP scope: granteeData capture (name + idCardNumber + phone + address)
 * + skipDocumentUpload-with-grace fallback (ADR-124 Am.1 #1). Full photo
 * upload deferred Sprint 18+ (MinIO adapter wiring).
 */

import { Card, CardContent, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Button } from '@kgc/ui';
import { useState } from 'react';
import {
  PENDING_ONE_TIME_SENTINEL,
  useWizardStore,
  type PendingOneTimeGrant,
} from '../wizard-store';

interface OneTimeGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grantorName: string;
}

interface GranteeDataForm {
  name: string;
  idCardNumber: string;
  phone: string;
  address: string;
}

const EMPTY_FORM: GranteeDataForm = {
  name: '',
  idCardNumber: '',
  phone: '',
  address: '',
};

function formToPending(form: GranteeDataForm): PendingOneTimeGrant {
  const out: PendingOneTimeGrant = { name: form.name.trim() };
  if (form.idCardNumber.trim()) out.idCardNumber = form.idCardNumber.trim();
  if (form.phone.trim()) out.phone = form.phone.trim();
  if (form.address.trim()) out.address = form.address.trim();
  return out;
}

export function OneTimeGrantDialog({ open, onOpenChange, grantorName }: OneTimeGrantDialogProps) {
  const { t } = useTranslation();
  const { setPendingOneTimeGrant, setGrantId, pendingOneTimeGrant } = useWizardStore();
  const [form, setForm] = useState<GranteeDataForm>(() => ({
    name: pendingOneTimeGrant?.name ?? '',
    idCardNumber: pendingOneTimeGrant?.idCardNumber ?? '',
    phone: pendingOneTimeGrant?.phone ?? '',
    address: pendingOneTimeGrant?.address ?? '',
  }));
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const closeAndReset = () => {
    setForm(EMPTY_FORM);
    setError(null);
    onOpenChange(false);
  };

  // Backdrop click — review P5 fix: close without destroying captured input.
  // The user may have mis-clicked; preserve form state for the next open.
  const closeKeepingForm = () => {
    setError(null);
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.name.trim().length === 0) {
      setError(t('rental.checkout.grant.oneTimeDialog.errors.nameRequired'));
      return;
    }

    setPendingOneTimeGrant(formToPending(form));
    setGrantId(PENDING_ONE_TIME_SENTINEL);
    closeAndReset();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={closeKeepingForm}
      role="dialog"
      aria-modal="true"
      aria-labelledby="grant-dialog-title"
      data-testid="one-time-grant-dialog"
    >
      <Card
        className="max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3
                id="grant-dialog-title"
                className="text-base font-bold text-gray-900 dark:text-white"
              >
                {t('rental.checkout.grant.oneTimeDialog.title')}
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-[#ccfbf1] mt-0.5">
                {t('rental.checkout.grant.oneTimeDialog.grantor', { name: grantorName })}
              </p>
            </div>
            <button
              type="button"
              onClick={closeAndReset}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('common.close')}
              data-testid="grant-dialog-close"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-[#ccfbf1] mb-1">
                {t('rental.checkout.grant.oneTimeDialog.nameLabel')}{' '}
                <span className="text-red-600">*</span>
              </label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={t('rental.checkout.grant.oneTimeDialog.namePlaceholder')}
                data-testid="grant-dialog-name"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-[#ccfbf1] mb-1">
                {t('rental.checkout.grant.oneTimeDialog.idCardLabel')}
              </label>
              <Input
                value={form.idCardNumber}
                onChange={e => setForm({ ...form, idCardNumber: e.target.value })}
                placeholder={t('rental.checkout.grant.oneTimeDialog.idCardPlaceholder')}
                data-testid="grant-dialog-idcard"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-[#ccfbf1] mb-1">
                {t('rental.checkout.grant.oneTimeDialog.phoneLabel')}
              </label>
              <Input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder={t('rental.checkout.grant.oneTimeDialog.phonePlaceholder')}
                data-testid="grant-dialog-phone"
              />
            </div>

            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-3 text-[11px] text-amber-800 dark:text-amber-200">
              ⚠️ {t('rental.checkout.grant.oneTimeDialog.gracePeriodWarning')}
            </div>

            {error && (
              <div
                className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-[11px] text-red-800 dark:text-red-200"
                data-testid="grant-dialog-error"
              >
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeAndReset}
                className="flex-1"
                data-testid="grant-dialog-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" className={cn('flex-1')} data-testid="grant-dialog-submit">
                {t('rental.checkout.grant.oneTimeDialog.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
